import { createSelector } from 'reselect'
import { toNumber } from 'lodash'
import * as forms from '../components/forms/form'
import * as sections from '../components/sections/section'
import { otherIncomeSectionId } from '../components/sections/Section7Income'

const CONSOLE_OUTPUT = false && process.env.NODE_ENV === 'development'

// monthly cost aggregates derived from store
//
// section 1 total
const section1Total = state =>
  toNumber(state.get('app').get(sections.getSectionTotalKey(1)))
// section 2 total
const section2Total = state =>
  toNumber(state.get('app').get(sections.getSectionTotalKey(2)))
// section 3 total
const section3Total = state =>
  toNumber(state.get('app').get(sections.getSectionTotalKey(3)))

// basics cost
export const basicsAggregateSelector = createSelector(
  section1Total,
  basics => basics
)
// extras cost
export const extrasAggregateSelector = createSelector(
  section1Total,
  section2Total,
  (basics, extras) => basics + extras
)
// freedom cost
export const freedomAggregateSelector = createSelector(
  section1Total,
  section2Total,
  section3Total,
  (basics, extras, freedom) => basics + extras + freedom
)

// real rate of return derived from store
//
// inflation
const inflation = state => state.get('forms').get(forms.inflationEstimateKey)
// pre-retirement investment return estimate
const preReturnRate = state => state.get('forms').get(forms.preAssetsReturnKey)
// pre-retirement real return rate
export const preRealRateSelector = createSelector(
  inflation,
  preReturnRate,
  (inflation, returnRate) => toNumber((returnRate - inflation).toFixed(2))
)
// post-retirement investment return estimate
const postReturnRate = state =>
  state.get('forms').get(forms.postAssetsReturnKey)
// post-retirement real return rate
export const postRealRateSelector = createSelector(
  inflation,
  postReturnRate,
  (inflation, returnRate) => toNumber((returnRate - inflation).toFixed(2))
)

// First year income net tax
//
// social security
const ssIncome = state => state.get('forms').get(forms.ssIncomeKey)
// pension
const pensionIncome = state => state.get('forms').get(forms.pensionIncomeKey)
// other income
const otherIncomeTotal = state =>
  state.get('app').get(sections.getSectionTotalKey(otherIncomeSectionId))
// years until retirement
const yearsUntilRetirement = state =>
  state.get('forms').get(forms.yearsUntilRetirementKey)

// BASIC
export const basicY1INetTaxSelector = createSelector(
  ssIncome,
  pensionIncome,
  otherIncomeTotal,
  basicsAggregateSelector,
  inflation,
  yearsUntilRetirement,
  getIncomeNeedNetTax
)

// EXTRAS
export const extrasY1INetTaxSelector = createSelector(
  ssIncome,
  pensionIncome,
  otherIncomeTotal,
  extrasAggregateSelector,
  inflation,
  yearsUntilRetirement,
  getIncomeNeedNetTax
)

// FREEDOM
export const freedomY1INetTaxSelector = createSelector(
  ssIncome,
  pensionIncome,
  otherIncomeTotal,
  freedomAggregateSelector,
  inflation,
  yearsUntilRetirement,
  getIncomeNeedNetTax
)

/**
 * Calculate an annual income need net of tax
 * @param ssIncome
 * @param pensionIncome
 * @param otherIncome
 * @param monthlyCost
 * @param inflation
 * @param years
 * @returns {number}
 */
function getIncomeNeedNetTax (
  ssIncome,
  pensionIncome,
  otherIncome,
  monthlyCost,
  inflation,
  years
) {
  if (CONSOLE_OUTPUT) {
    console.log(
      `ss=${ssIncome}, pension=${pensionIncome}, other=${otherIncome}, monthlyCost=${monthlyCost}, inflation=${inflation}, years=${years}`
    )
  }

  const preInflationAnnualNeed =
    (monthlyCost - (ssIncome + pensionIncome + otherIncome)) * 12

  if (CONSOLE_OUTPUT) {
    console.log(`preInflationAnnualNeed=${preInflationAnnualNeed}`)
  }

  const total = (preInflationAnnualNeed *
    Math.pow(1 + inflation, years)).toFixed(2)
  return toNumber(total >= 0 ? total : 0)
}

// Gross first year income need
//
// tax rate
const taxRate = state => state.get('forms').get(forms.taxRateKey)

// basics gross y1 income
export const basicGrossY1ISelector = createSelector(
  taxRate,
  basicY1INetTaxSelector,
  getGrossIncomeNeed
)

// extras gross y1 income
export const extrasGrossY1ISelector = createSelector(
  taxRate,
  extrasY1INetTaxSelector,
  getGrossIncomeNeed
)

// freedom gross y1 income
export const freedomGrossY1ISelector = createSelector(
  taxRate,
  freedomY1INetTaxSelector,
  getGrossIncomeNeed
)

/**
 * Calculate annual income need gross
 * @param taxRate
 * @param y1INetTax
 * @returns {number}
 */
function getGrossIncomeNeed (taxRate, y1INetTax) {
  return toNumber((y1INetTax * taxRate + y1INetTax).toFixed(2))
}

// Year 1 value of years-in-retirement annuity
//
// years in retirement
const yearsInRetirement = state =>
  state.get('forms').get(forms.yearsInRetirementKey)

export const basicYear1AnnuityValueSelector = createSelector(
  basicGrossY1ISelector,
  postRealRateSelector,
  yearsInRetirement,
  year1AnnuityValue
)

export const extrasYear1AnnuityValueSelector = createSelector(
  extrasGrossY1ISelector,
  postRealRateSelector,
  yearsInRetirement,
  year1AnnuityValue
)

export const freedomYear1AnnuityValueSelector = createSelector(
  freedomGrossY1ISelector,
  postRealRateSelector,
  yearsInRetirement,
  year1AnnuityValue
)

/**
 * Calculate the year 1 value of the retirment annuity required for an income
 * level
 * @param grossYear1Income
 * @param realRate
 * @param years
 */
export function year1AnnuityValue (grossYear1Income, realRate, years) {
  if (CONSOLE_OUTPUT) {
    console.log(
      `grossYear1Income=${grossYear1Income}, realRate=${realRate}, years=${years}`
    )
  }
  let usableRate = realRate
  if (usableRate === 0) {
    usableRate = 0.00001
  }
  const af1 = 1 / usableRate
  const af2 = 1 / (usableRate * Math.pow(1 + usableRate, years))
  if (CONSOLE_OUTPUT) {
    console.log(`af1=${af1}, af2=${af2}`)
  }
  return toNumber((grossYear1Income * (af1 - af2)).toFixed(2))
}

// Future value of savings
//
// tax-deferred savings
const taxDeferredSavings = state =>
  state.get('forms').get(forms.taxDeferredSavingsKey)

const taxExemptSavings = state =>
  state.get('forms').get(forms.taxFreeSavingsKey)

// total current capital
export const currentCapitalSelector = createSelector(
  taxDeferredSavings,
  taxExemptSavings,
  (deferredSavings, exemptSavings) =>
    toNumber((deferredSavings + exemptSavings).toFixed(2))
)

// future value of total current capital
export const futureValueCurrentCapitalSelector = createSelector(
  currentCapitalSelector,
  preRealRateSelector,
  yearsUntilRetirement,
  (currentCapital, realRate, years) => {
    return toNumber((currentCapital * Math.pow(1 + realRate, years)).toFixed(2))
  }
)

// future value of additional capital required
// Basic
export const fvBasicAdditionalCapitalSelector = createSelector(
  basicYear1AnnuityValueSelector,
  futureValueCurrentCapitalSelector,
  futureValueAdditionalCapital
)

export const fvExtrasAdditionalCapitalSelector = createSelector(
  extrasYear1AnnuityValueSelector,
  futureValueCurrentCapitalSelector,
  futureValueAdditionalCapital
)

export const fvFreedomAdditionalCapitalSelector = createSelector(
  freedomYear1AnnuityValueSelector,
  futureValueCurrentCapitalSelector,
  futureValueAdditionalCapital
)

/**
 * Future value of retirement annuity minus future value of current capital
 * @param fvTotal
 * @param fvCurrentCapital
 * @returns {number}
 */
function futureValueAdditionalCapital (fvTotal, fvCurrentCapital) {
  if (CONSOLE_OUTPUT) {
    console.log(`fvTotal=${fvTotal}, fvCurrentCapital=${fvCurrentCapital}`)
  }
  return toNumber((fvTotal - fvCurrentCapital).toFixed(2))
}

// Present value of additional capital need
// Basic
export const pvBasicAdditionalCapitalSelector = createSelector(
  fvBasicAdditionalCapitalSelector,
  preReturnRate,
  yearsUntilRetirement,
  pvAdditionalCapital
)

export const pvExtrasAdditionalCapitalSelector = createSelector(
  fvExtrasAdditionalCapitalSelector,
  preReturnRate,
  yearsUntilRetirement,
  pvAdditionalCapital
)

export const pvFreedomAdditionalCapitalSelector = createSelector(
  fvFreedomAdditionalCapitalSelector,
  preReturnRate,
  yearsUntilRetirement,
  pvAdditionalCapital
)

/**
 * Future value of additional capital
 * @param fvAdditionalCapital
 * @param realRate
 * @param years
 * @returns {number}
 */
function pvAdditionalCapital (fvAdditionalCapital, realRate, years) {
  return toNumber(
    (fvAdditionalCapital / Math.pow(1 + realRate, years)).toFixed(2)
  )
}

// PV monthly savings
// Basic
export const basicMonthlySavingsSelector = createSelector(
  pvBasicAdditionalCapitalSelector,
  yearsUntilRetirement,
  monthlyCost
)

// Extras
export const extrasMonthlySavingsSelector = createSelector(
  pvExtrasAdditionalCapitalSelector,
  yearsUntilRetirement,
  monthlyCost
)

// Freedom
export const freedomMonthlySavingsSelector = createSelector(
  pvFreedomAdditionalCapitalSelector,
  yearsUntilRetirement,
  monthlyCost
)

/**
 * Given lump total, divide it up by years & months to monthly savings need
 * @param pvAdditionalCapital
 * @param years
 * @returns {number}
 */
export function monthlyCost (pvAdditionalCapital, years) {
  if (years === 0) {
    return 0
  }
  return toNumber((pvAdditionalCapital / years / 12).toFixed(2))
}



// WEBPACK FOOTER //
// ./src/redux/selectors.js